import type { components } from "./schema.gen";

/** The contract's schemas, generated from backend/openapi/openapi.yaml (`pnpm gen:api`). */
export type Schemas = components["schemas"];

/**
 * A request the API answered with an RFC 9457 problem, or one that never
 * reached it (`status` 0). The message is safe to log; screens show their own
 * copy, never the server's diagnostics.
 */
export class ApiProblem extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    /** Seconds to wait, from Retry-After on a 429. */
    readonly retryAfterSeconds?: number,
    /** Per-item reasons of a 422 catalogue_changes_refused. */
    readonly refusals?: Schemas["Problem"]["refusals"],
  ) {
    super(`API request failed: ${status} ${code}`);
    this.name = "ApiProblem";
  }

  get unreachable(): boolean {
    return this.status === 0;
  }
}

type RequestOptions = {
  body?: unknown;
  /** The synchronizer token, kept in memory only and sent as X-CSRF-Token. */
  csrfToken?: string;
  /** A fresh, single-use step-up for a sensitive action (X-Reauthorization-ID). */
  reauthorizationId?: string;
  /** An idempotent command's UUIDv7, kept across retries (X-Operation-ID). */
  operationId?: string;
  signal?: AbortSignal;
};

/**
 * Where the API answers. In development the Vite server proxies /v1, so the
 * page and the API share one origin. A production build talks to the API's
 * own sibling origin (P6-008 decision 30: the app at https://app.burgepos.com,
 * the API at https://api.burgepos.com). VITE_API_ORIGIN overrides it for a
 * trial host or the cross-origin browser test.
 */
export const apiOrigin: string = import.meta.env.VITE_API_ORIGIN ?? (import.meta.env.PROD ? "https://api.burgepos.com" : "");

/**
 * Calls the API. The session and sign-in transaction cookies are host-only on
 * the API's origin, HttpOnly and SameSite=Strict, so script never sees them.
 * The app is a same-site sibling of the API, so "include" sends them; the API
 * grants credentialed CORS to the one allowed origin only.
 */
export async function apiRequest<T>(method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE", path: `/v1/${string}`, options: RequestOptions = {}): Promise<T> {
  const headers = requestHeaders("application/json, application/problem+json", options);

  let response: Response;
  try {
    response = await fetch(`${apiOrigin}${path}`, {
      body: bodyOf(options.body),
      credentials: "include",
      headers,
      method,
      signal: options.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }
    throw new ApiProblem(0, "unreachable");
  }

  if (response.ok) {
    return (response.status === 204 ? undefined : await response.json()) as T;
  }
  throw await problemOf(response);
}

/** A file (a logo upload, P5-014) is sent as itself, with its own type; anything else as JSON. */
function bodyOf(body: unknown): BodyInit | undefined {
  return body === undefined ? undefined : body instanceof Blob ? body : JSON.stringify(body);
}

/** A request's headers: what it accepts, its body type, token, step-up and operation. */
function requestHeaders(accept: string, options: RequestOptions): Record<string, string> {
  const headers: Record<string, string> = { Accept: accept };
  if (options.body !== undefined) {
    headers["Content-Type"] = options.body instanceof Blob ? options.body.type : "application/json";
  }
  if (options.csrfToken) {
    headers["X-CSRF-Token"] = options.csrfToken;
  }
  if (options.reauthorizationId) {
    headers["X-Reauthorization-ID"] = options.reauthorizationId;
  }
  if (options.operationId) {
    headers["X-Operation-ID"] = options.operationId;
  }
  return headers;
}

/** The ApiProblem for a refused response, keeping its stable code and Retry-After. */
async function problemOf(response: Response): Promise<ApiProblem> {
  let code = "unexpected_response";
  let refusals: Schemas["Problem"]["refusals"];
  try {
    const problem = (await response.json()) as Partial<Schemas["Problem"]>;
    if (typeof problem.code === "string") {
      code = problem.code;
    }
    if (Array.isArray(problem.refusals)) refusals = problem.refusals;
  } catch {
    // A body that is not a problem keeps the generic code.
  }
  const retryAfter = Number(response.headers.get("Retry-After"));
  return new ApiProblem(response.status, code, Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined, refusals);
}

/**
 * Downloads a file the API serves, such as a Z report as CSV, with the
 * session cookie. A GET read needs no synchronizer token; a POST download
 * (a period report export, P5-013 W4) carries its token, step-up and
 * operation like any command. Refusals reject with ApiProblem, as apiRequest
 * does.
 */
export async function apiDownload(
  path: `/v1/${string}`,
  accept: string,
  options: RequestOptions & { method?: "GET" | "POST" } = {},
): Promise<{ blob: Blob; filename: string | null }> {
  let response: Response;
  try {
    response = await fetch(`${apiOrigin}${path}`, {
      body: bodyOf(options.body),
      credentials: "include",
      headers: requestHeaders(accept, options),
      method: options.method ?? "GET",
    });
  } catch {
    throw new ApiProblem(0, "unreachable");
  }
  if (!response.ok) {
    throw await problemOf(response);
  }
  const disposition = response.headers.get("Content-Disposition") ?? "";
  return { blob: await response.blob(), filename: /filename="([^"]+)"/.exec(disposition)?.[1] ?? null };
}
